import { escapeRegExp, setByPath } from 'quival/src/helpers.js';

// Get attribute path in dot notation from attribute name
function getAttributePath(name) {
    return name.replace(/\]\[/gm, '.').replace('[', '.').replace(']', '').replace(/\.$/, '');
}

// Get attribute name from attribute path
function getAttributeName(path) {
    return path.includes('.') ? path.split('.').reduce((accumulator, value) => `${accumulator}[${value}]`) : path;
}

// Get correct rule path from the rules
function getRulePath(stringified, name) {
    const path = getAttributePath(name);

    if (stringified.includes(`"${path}"`)) {
        return path;
    } else {
        if (!path.includes('.')) {
            return null;
        }
    }

    const parts = path.split('.');
    let pattern = `"(${escapeRegExp(parts[0])}`;

    for (const part of parts.slice(1)) {
        pattern += `\\.(${escapeRegExp(part)}|\\*)`;
    }

    pattern += `)"`;

    return (stringified.match(new RegExp(pattern)) ?? [null, null])[1];
}

// Get the value from the element by name
function getValue(root, elements) {
    if (typeof elements === 'string') {
        const attributeName = getAttributeName(elements);
        elements = root.querySelectorAll(`[name="${attributeName}"]:not([type="hidden"]), [name="${attributeName}[]"]:not([type="hidden"])`);
    }

    if (elements.length === 0) return '';

    const element = elements[0];
    let value = '';

    if (element.type === 'checkbox') {
        if (element.name.endsWith('[]') || elements.length > 1) {
            value = Array.prototype.map.call(elements, (element) => (element.checked ? element.value : '')).filter((value) => value !== '');
        } else {
            value = element.checked ? element.value : '';
        }
    } else if (element.type === 'radio') {
        for (const element of elements) {
            if (element.checked) {
                value = element.value;
                break;
            }
        }
    } else if (element.type === 'select-multiple') {
        value = Array.prototype.map.call(element.selectedOptions, (option) => option.value);
    } else if (element.type === 'file') {
        if (element.multiple) {
            value = Array.from(element.files);
        } else {
            value = element.files[0];
        }
    } else if (element.type === 'number' || element.type === 'range') {
        value = parseFloat(element.value, 10);

        if (isNaN(value)) {
            value = null;
        }
    } else {
        value = element.value;
    }

    return value;
}

// Get input elements
function inputElements(root) {
    return root.querySelectorAll('input:not([type="hidden"]), select, textarea');
}

// Collect values for all elements
function collectValues(root, attributes) {
    const elements = Array.from(inputElements(root));
    const data = {};

    for (const attribute of attributes) {
        const attributeName = getAttributeName(attribute);
        const filtered = elements.filter((element) => element.name === attributeName || element.name === attributeName + '[]');
        const value = getValue(root, filtered);

        setByPath(data, attribute, value);
    }

    return data;
}

// Get all attributes in the form
function getAllAttributes(root) {
    const elements = inputElements(root);
    const attributes = {};

    elements.forEach((element) => {
        if (element.name === '') return;

        attributes[getAttributePath(element.name)] = true;
    });

    return Object.keys(attributes);
}

// Add / remove spin animation on focusin / focusout event
function requiredAsteriskAnimation(event) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof event.target.name !== 'string' || event.target.name === '') return;

    const name = event.target.name.replace(/^_/, '');
    const element = document.querySelector(`label[for="${name}"] .required`);

    if (!element) return;

    if (event.type === 'focusin') {
        element.classList.add('fa-spin');
    } else if (event.type === 'focusout') {
        element.classList.remove('fa-spin');
    }
}

// Prepare rules for precognition
function preparePrecognitionRules(rules) {
    let found = false;

    for (let i = 0; i < rules.length; i++) {
        const rule = rules[i];

        if (/^(active_url|exists|unique)/.test(rule)) {
            found = true;
            rules.splice(i, 1);
        }
    }

    if (found) rules.push('precognition');

    return rules;
}

// Form component
export default function (...extenders) {
    const _validator = new Validator().alwaysBail();
    let root;
    let _submitButtons = [];
    let _lastAllValidatedTime = 0;
    const _lastValidatedTimes = {};
    const _precognitionCache = {};

    const component = {
        scrollFocusEnabled: true,
        scrollFocusOffset: 20,
        disableSubmitValidation: false,

        events: {
            'x-on:focusin': (event) => requiredAsteriskAnimation(event),
            'x-on:focusout': (event) => requiredAsteriskAnimation(event),
            'x-on:change'(event) {
                return this.validateField(event);
            },
            'x-on:input.debounce.1000'(event) {
                return this.validateField(event);
            },
            'x-on:revalidate.document'(event) {
                return this.validateField(event);
            },
            async 'x-on:submit'(event) {
                event.preventDefault();

                this.showSpinnerInSubmitButton(true, event.submitter);

                if (!((await this.validateAllFields()) && (await this.extraValidations()))) {
                    this.hideSpinnerInSubmitButton();

                    return;
                }

                const postResponseAction = (response) => {
                    if (Object.hasOwn(response.headers, 'x-bolt')) {
                        window.Bolt.once('load', () => window.Bolt.preserveScroll(Object.keys(window.App.errors ?? {}).length > 0));
                    } else {
                        this.hideSpinnerInSubmitButton();
                    }

                    axios.interceptors.response.eject(interceptor);
                };

                const interceptor = axios.interceptors.response.use(
                    (response) => {
                        postResponseAction(response);

                        return response;
                    },
                    (error) => {
                        postResponseAction(error.response);

                        return Promise.reject(error);
                    },
                    {
                        synchronous: true,
                    }
                );

                await this.submit(event);
            },
        },

        action: null,
        method: 'POST',
        data: {},
        attributes: [],
        errors: {},
        rules: {},
        customMessages: {},
        customAttributes: {},
        customValues: {},
        stringifiedRuleNames: '',
        showErrorList: false,

        get filteredErrors() {
            return Object.fromEntries(Object.entries(this.errors).filter(([attribute, message]) => message.length > 0));
        },

        get errorCount() {
            return Object.values(this.errors).filter((message) => message).length;
        },

        // Initialization on page load
        init() {
            root = this.$root;

            // Bind events
            root.setAttribute('x-bind', 'events');

            // Disable form native validation
            root.setAttribute('novalidate', '');

            // Action & method
            this.action = this.action ?? root.getAttribute('action') ?? window.location.href;
            root.setAttribute('x-bind:action', 'action');

            const methodElement = root.querySelector('[name="_method"]');

            if (methodElement) {
                this.method = methodElement.value || 'POST';
                methodElement.setAttribute('x-bind:value', 'method');
            }

            // Get top offset for scroll focus
            this.scrollFocusOffset = window.App?.topOffset ?? this.scrollFocusOffset;

            // Set errors
            this.setErrors(window.App.errors ?? {}, true);

            // If formRequest property exists, use it to populate rules, custom error messages, custom attributes and custom values
            if (typeof this.formRequest !== 'undefined') {
                this.rules = this.formRequest.rules;
                this.customMessages = this.formRequest.messages;
                this.customAttributes = this.formRequest.attributes;
                this.customValues = this.formRequest.values;
            }

            // Stringified rule names
            this.stringifiedRuleNames = JSON.stringify(Object.keys(this.rules));

            // Refresh stringified rule names when rules changed
            this.$watch('rules', (value) => (this.stringifiedRuleNames = JSON.stringify(Object.keys(value))));

            // Get all attributes
            this.refreshAttributes();

            // Handle dynamic fields
            root.querySelectorAll('[x-if], [x-for]').forEach((template) => {
                const content = template.content;

                content.querySelectorAll('[x-bind\\:name]').forEach((element) => {
                    let dynamicName = element.getAttribute('x-bind:name');

                    if (dynamicName.startsWith('addField(')) return;

                    element.setAttribute('x-bind:name', `addField(${dynamicName})`);
                });

                content.querySelectorAll('[name]').forEach((element) => {
                    if (element.hasAttribute('x-bind:name')) return;

                    element.setAttribute('x-bind:name', `addField('${element.getAttribute('name')}')`);
                });
            });

            // Modify submit button
            this.$nextTick(() => {
                _submitButtons = root.querySelectorAll('button[type="submit"]');

                if (_submitButtons.length > 0) {
                    _submitButtons.forEach(
                        (button) => (button.innerHTML += `<span class="d-none fas fa-spinner fa-spin ${IS_BOOTSTRAP5 ? 'ms-1' : 'ml-1'}"></span>`)
                    );
                }
            });

            // Run postInit, if available
            if (typeof this.postInit !== 'undefined') {
                this.postInit();
            }
        },

        // Recollect all attributes
        refreshAttributes() {
            this.attributes = getAllAttributes(root);
        },

        // Remove attribute and error when field is removed
        fieldRemoved(element) {
            const attribute = getAttributePath(element.getAttribute('name'));
            this.attributes.splice(this.attributes.indexOf(attribute), 1);
            delete this.errors[attribute];
        },

        // Add field for validation
        addField(attribute) {
            attribute = getAttributePath(attribute);

            const attributeName = getAttributeName(attribute);

            if (!this.attributes.includes(attribute)) {
                this.attributes.push(attribute);

                this.$nextTick(() => root.querySelector(`[name="${attributeName}"]`)?.setAttribute('x-removed', 'fieldRemoved($el)'));
            }

            return attributeName;
        },

        // Highlight valid and invalid fields
        highlightField(attribute) {
            attribute = getAttributePath(attribute);

            if (Object.hasOwn(this.errors, attribute)) {
                return this.errors[attribute] === '' ? 'is-valid' : 'is-invalid';
            }

            return '';
        },

        // Show the field
        showField(attribute) {
            this.gotoField(attribute);
        },

        // Go to the field
        gotoField(attribute) {
            const attributeName = getAttributeName(attribute);
            let element = root.querySelector(`[name="${attributeName}"]`);

            if (!element) {
                element = root.querySelector(`[error-for="${attributeName}"]`);
            }

            if (!element) return;

            if (element.dataset.customFocus) {
                element = root.querySelector(element.dataset.customFocus);
            }

            const setFocus = (element) => {
                const xData = Helper.getAlpineData(element);

                if (xData && !xData.modalId && xData.focus) {
                    xData.focus();
                } else {
                    element.focus();
                }
            };

            if (this.scrollFocusEnabled) {
                let container = this.isInModal ? root.closest('.modal') : window;
                let elementTop = element.getBoundingClientRect().top;
                let startPos = typeof container.scrollY === 'number' ? container.scrollY : container.scrollTop;
                let endPos = startPos + elementTop - this.scrollFocusOffset;
                let duration = Math.max(150, Math.min(Math.ceil(Math.abs(endPos - startPos) / 12), 500));

                Helper.scrollToElement(element, duration, this.scrollFocusOffset, (element) => setFocus(element), container);
            } else {
                element.scrollIntoView();
                setFocus(element);
            }
        },

        // Validate an attribute
        async validateField(event) {
            if (Date.now() - _lastAllValidatedTime < 1000) return;

            let attribute;
            let isRevalidate = false;

            if (event.type === 'change' || event.type === 'input') {
                if (!event.target.name) return;

                attribute = getAttributePath(event.target.name);
            } else if (event.type === 'revalidate') {
                attribute = event.detail.attribute;
                isRevalidate = true;
            }

            if (!attribute || (Date.now() - _lastValidatedTimes[attribute] ?? 0) < 1000) return;

            const rulePath = getRulePath(this.stringifiedRuleNames, attribute);

            if (rulePath === null) return;

            const rules = {};
            rules[attribute] = Array.from(this.rules[rulePath]);

            // Prepare rules for precognition
            rules[attribute] = preparePrecognitionRules(rules[attribute]);
            const precognition = rules[attribute].includes('precognition');

            // Handle confirmed rule and confirmation field
            if (!isRevalidate) {
                if (attribute.endsWith('_confirmation')) {
                    this.$nextTick(() => Helper.dispatchCustomEvent('revalidate', { attribute: attribute.slice(0, attribute.lastIndexOf('_')) }));
                } else if (rules[attribute].includes('confirmed')) {
                    let confirmationAttribute = attribute + '_confirmation';
                    let confirmationValue = getValue(root, confirmationAttribute);

                    if (confirmationValue.length === 0) {
                        rules[attribute].splice(rules[attribute].indexOf('confirmed'), 1);
                    } else {
                        this.$nextTick(() => Helper.dispatchCustomEvent('revalidate', { attribute: confirmationAttribute }));
                    }
                }
            }

            _lastValidatedTimes[attribute] = Date.now();

            const validator = this.getValidator(rules);

            if (rulePath.includes('*')) {
                validator.addImplicitAttribute(attribute, rulePath);
            }

            await validator.passes();
            const errorBag = validator.errors();

            if (errorBag.isEmpty() && precognition) {
                const error = (await this.precognitionCall([attribute]))[attribute];

                if (error.length > 0) {
                    errorBag.add(attribute, error[0]);
                } else {
                    errorBag.remove(attribute);
                }
            }

            this.setError(attribute, errorBag.isEmpty() ? '' : errorBag.first(attribute));
        },

        // Validate all attributes
        async validateAllFields(passes = () => {}, fails = () => {}) {
            if (this.disableSubmitValidation) return true;

            const rules = {};
            const rulePaths = {};
            const precognitionAttributes = [];

            _lastAllValidatedTime = Date.now();

            const collectRules = (attribute) => {
                const rulePath = getRulePath(this.stringifiedRuleNames, attribute);

                if (rulePath) {
                    if (!Object.hasOwn(rules, rulePath)) {
                        rules[rulePath] = Array.from(this.rules[rulePath]);
                    }

                    rulePaths[attribute] = rulePath;
                }
            };

            this.refreshAttributes();

            for (const attribute of this.attributes) {
                if (attribute.includes('.')) {
                    const partialAttribute = [];

                    for (const part of attribute.split('.')) {
                        partialAttribute.push(part);
                        collectRules(partialAttribute.join('.'));
                    }
                } else {
                    collectRules(attribute);
                }

                const rulePath = rulePaths[attribute] ?? null;

                if (rulePath === null) continue;

                rules[rulePath] = preparePrecognitionRules(rules[rulePath]);

                if (rules[rulePath].includes('precognition')) {
                    precognitionAttributes.push(attribute);
                }
            }

            const validator = this.getValidator(rules);
            await validator.passes();
            const errorBag = validator.errors();
            const skippedAttributes = validator.skippedAttributes();

            const precognitionErrors = await this.precognitionCall(
                precognitionAttributes.filter((attribute) => !errorBag.has(attribute) && !skippedAttributes.includes(attribute))
            );

            for (const [attribute, messages] of Object.entries(precognitionErrors)) {
                if (messages.length > 0) {
                    errorBag.add(attribute, messages[0]);
                } else {
                    errorBag.remove(attribute);
                }
            }

            const success = errorBag.isEmpty();

            if (success) {
                const errors = {};

                for (const attribute of Object.keys(rulePaths)) {
                    errors[attribute] = '';
                }

                this.setErrors(errors);

                passes();
            } else {
                const errors = {};

                for (const attribute of Object.keys(rulePaths)) {
                    errors[attribute] = errorBag.has(attribute) ? errorBag.first(attribute) : '';
                }

                this.setErrors(errors, true);

                fails();
            }

            return success;
        },

        // Perform precognition call for given attributes
        async precognitionCall(attributes) {
            const data = {};
            const dependentData = this.precognitionDependentData() ?? {};
            let cacheKeys = {};
            let cachedCount = 0;

            for (const [key, cached] of Object.entries(_precognitionCache)) {
                if (Date.now() - cached.time >= 10000) {
                    delete _precognitionCache[key];
                }
            }

            for (let i = 0; i < attributes.length; i++) {
                const attribute = attributes[i];
                const value = getValue(root, attribute);
                const dependent = dependentData[attribute] ?? {};
                const cacheKey = `${getRulePath(this.stringifiedRuleNames, attribute)}|${value}|${JSON.stringify(dependent)}`;
                const cached = _precognitionCache[cacheKey] ?? null;

                cacheKeys[attribute] = cacheKey;

                if (cached && Date.now() - cached.time < 10000) {
                    cachedCount++;
                }

                if (value === null || value === '') {
                    continue;
                }

                setByPath(data, attribute, value);

                if (dependent) {
                    Object.assign(data, dependent);
                }
            }

            let result = {};

            if (Object.keys(data).length > 0 && attributes.length !== cachedCount) {
                try {
                    await axios[this.method.toLowerCase()](this.action, data, {
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            Precognition: 'true',
                            'Precognition-Exclude-Dependent-Rules': 'true',
                            'Precognition-Validate-Only': attributes.join(','),
                        },
                    });

                    for (const attribute of attributes) {
                        result[attribute] = [];
                    }
                } catch (error) {
                    if (Object.hasOwn(error, 'response')) {
                        result = error.response?.data?.errors ?? {};
                    } else {
                        console.error(error);
                    }
                }
            }

            const errors = {};

            for (const attribute of attributes) {
                if (!Object.hasOwn(_precognitionCache, cacheKeys[attribute])) {
                    _precognitionCache[cacheKeys[attribute]] = {
                        attribute,
                        messages: result[attribute] ?? [],
                        time: Date.now(),
                    };
                }

                errors[attribute] = _precognitionCache[cacheKeys[attribute]].messages;
            }

            return errors;
        },

        // Get precognition dependent data
        precognitionDependentData() {
            return {};
        },

        // Get Validator
        getValidator(rules) {
            return _validator.setProperties(collectValues(root, this.attributes), rules, this.customMessages, this.customAttributes, this.customValues);
        },

        // Set custom error to an attribute
        setCustomError(attribute, error) {
            return this.setError(attribute, error);
        },

        // Set an error to the attribute
        setError(attribute, error) {
            attribute = getAttributePath(attribute);

            this.errors[attribute] = error;
            this.showErrorsAndHighlightFields(attribute);
        },

        // Set errors
        setErrors(errors, jump = false) {
            let firstAttribute,
                errorCount = 0;

            for (const [attribute, error] of Object.entries(errors)) {
                if (Array.isArray(error)) {
                    errors[attribute] = error[0];
                }

                if (errors[attribute]) {
                    firstAttribute ??= attribute;
                    errorCount++;
                }
            }

            this.errors = Object.assign({}, errors);
            this.showErrorList = errorCount > 0;

            this.$nextTick(() => {
                this.showErrorsAndHighlightFields();

                if (jump && errorCount > 0) {
                    this.gotoField(firstAttribute);
                }
            });
        },

        // Get the error for the attribute
        getError(attribute) {
            return this.errors[attribute] ?? '';
        },

        // Show errors and highlight related fields
        showErrorsAndHighlightFields(attributes = this.attributes) {
            if (typeof attributes === 'string') {
                attributes = [attributes];
            }

            const elements = Array.from(root.querySelectorAll('input:not([type="hidden"]), select, textarea, label[for], [error-for]'));
            const errorMessageElements = {};
            const validElements = [];
            const invalidElements = [];

            for (const element of elements) {
                const tagName = element.tagName.toLowerCase();
                let attribute;

                if (['input', 'select', 'textarea'].includes(tagName)) {
                    let attributeName;

                    if (element.hasAttribute('error-for')) {
                        attributeName = element.getAttribute('error-for');
                    } else {
                        attributeName = element.name;
                    }

                    attribute = getAttributePath(attributeName.replace(/\[\]$/, ''));
                } else if (tagName === 'label' && element.hasAttribute('for')) {
                    attribute = getAttributePath(element.getAttribute('for').replace(/\[\]$/, ''));
                } else if (element.hasAttribute('error-for')) {
                    attribute = getAttributePath(element.getAttribute('error-for'));
                }

                if (!Object.hasOwn(this.errors, attribute)) continue;

                if (this.errors[attribute] === '') {
                    validElements.push(element);
                } else {
                    invalidElements.push(element);

                    if (element.hasAttribute('error-for') && element.nextElementSibling && element.nextElementSibling.className.includes('invalid-feedback')) {
                        errorMessageElements[attribute] = element.nextElementSibling;
                    }
                }
            }

            for (const [attribute, element] of Object.entries(errorMessageElements)) {
                element.innerHTML = this.errors[attribute];
            }

            for (const element of validElements) {
                element.classList.add('is-valid');
                element.classList.remove('is-invalid');
            }

            for (const element of invalidElements) {
                element.classList.add('is-invalid');
                element.classList.remove('is-valid');
            }
        },

        // Reset form's state
        resetState() {
            this.errors = {};
            this.resetFieldHighlights();
        },

        // Reset field highlights
        resetFieldHighlights() {
            root.querySelectorAll('.is-valid, .is-invalid').forEach((element) => element.classList.remove('is-valid', 'is-invalid'));
        },

        // Show spinner in submit button
        showSpinnerInSubmitButton(show = true, submitter = null) {
            if (_submitButtons.length === 0) return;

            _submitButtons.forEach((button) => {
                button.disabled = show;

                if (submitter instanceof HTMLElement && !button.isEqualNode(submitter)) return;

                button.querySelector('.fa-spinner')?.classList[show ? 'remove' : 'add']('d-none');
            });
        },

        // Hide spinner in submit button
        hideSpinnerInSubmitButton() {
            this.showSpinnerInSubmitButton(false);
        },

        // Define extra validations that will be run before the form is submitted
        extraValidations() {
            return true;
        },

        // Submit the form after all attributes are validated
        async submit(event) {
            if (window.Bolt && window.Bolt.enabled) {
                await window.Bolt.submitForm(event.target, event.submitter);
            } else {
                event.target.submit();
            }
        },
    };

    extenders.unshift(component);

    return Helper.mergeAlpineComponents.apply(Helper, extenders);
}
